import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GoogleLogo } from "@phosphor-icons/react";
import { WorkMeshMark } from "@/components/WorkMeshLogo";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("admin@taskbazaar.com");
  const [password, setPassword] = useState("Admin@123");
  const [err, setErr] = useState("");
  const [needsVerify, setNeedsVerify] = useState(false);
  const [busy, setBusy] = useState(false);

  const urlErr = new URLSearchParams(location.search).get("error");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setNeedsVerify(false);
    setBusy(true);
    try {
      await login(email, password);
      navigate("/dashboard");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "EMAIL_NOT_VERIFIED") {
        setNeedsVerify(true);
        setErr(detail.msg || "Your email address has not been verified.");
      } else {
        setErr(formatApiErrorDetail(detail) || e?.message);
      }
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background text-foreground">
      {/* Left: visual */}
      <div className="hidden lg:block relative bg-background text-foreground overflow-hidden border-r border-border">
        <div className="relative z-10 h-full flex flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <WorkMeshMark size={40} />
            <div>
              <div className="text-2xl font-bold tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', 'Satoshi', sans-serif" }}>
                Work<span className="text-[#E1252A]">Mesh</span>
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">Internal Marketplace</div>
            </div>
          </div>
          <div className="space-y-6">
            <h1 className="text-4xl lg:text-5xl tracking-tight font-bold leading-tight max-w-md">
              Where your team's work finds the right people.
            </h1>
            <p className="text-sm text-zinc-500 max-w-md leading-relaxed">
              Post tasks, discover opportunities across departments, earn credits, build reputation —
              and put hidden talent to work.
            </p>
            <div className="grid grid-cols-3 gap-4 max-w-md">
              <div className="border border-border p-3">
                <div className="text-2xl font-bold text-[#E1252A] font-mono">10k</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">credits</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-2xl font-bold font-mono">5</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">depts</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-2xl font-bold font-mono">8+</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">open tasks</div>
              </div>
            </div>
          </div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">
            A VeltrixAI product · SSO ready
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 lg:p-12">
        <form className="w-full max-w-sm space-y-6" onSubmit={submit} data-testid="login-form">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-2">Welcome back</div>
            <h2 className="text-2xl sm:text-3xl tracking-tight font-semibold">Sign in to WorkMesh</h2>
          </div>

          {(err || urlErr) && (
            <div className="text-sm text-red-600 border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-950/30 px-3 py-2 rounded-sm" data-testid="login-error">
              {err || "Google sign-in failed. Please try again."}
              {needsVerify && (
                <button
                  type="button"
                  onClick={() => navigate(`/verify-email?email=${encodeURIComponent(email)}`)}
                  className="block mt-2 underline underline-offset-4 font-medium"
                  data-testid="login-verify-email-button"
                >
                  Verify Email →
                </button>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="email" className="text-[10px] uppercase tracking-[0.2em] font-bold">Email</Label>
            <Input
              id="email"
              data-testid="login-email-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="rounded-sm"
            />
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="password" className="text-[10px] uppercase tracking-[0.2em] font-bold">Password</Label>
              <Link to="/forgot-password" className="text-[11px] text-zinc-500 hover:text-foreground underline underline-offset-4" data-testid="link-forgot-password">
                Forgot password?
              </Link>
            </div>
            <Input
              id="password"
              data-testid="login-password-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="rounded-sm"
            />
          </div>

          <Button
            type="submit"
            disabled={busy}
            className="w-full rounded-sm bg-foreground text-background hover:bg-zinc-800 dark:hover:bg-zinc-200"
            data-testid="login-submit-button"
          >
            {busy ? "Signing in…" : "Sign in"}
          </Button>

          <div className="flex items-center gap-3">
            <div className="h-px bg-border flex-1" />
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">or</div>
            <div className="h-px bg-border flex-1" />
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            className="w-full rounded-sm"
            data-testid="login-google-button"
          >
            <GoogleLogo size={16} className="mr-2" /> Continue with Google
          </Button>

          <p className="text-sm text-zinc-500">
            New here?{" "}
            <Link to="/register" className="underline underline-offset-4 text-foreground" data-testid="link-register">
              Create an account
            </Link>
          </p>

          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400 border-t border-border pt-4">
            Demo credentials prefilled — Admin · Manager · Owner · Reviewer · Employee
          </div>
        </form>
      </div>
    </div>
  );
}
